export type HistoryFilters = {
  wallet: string; tx: string; from: string; to: string;
  side: string; status: string; dex: string; path: string;
  mode: string; minUsd: string; maxUsd: string; order: string;
};

export const emptyHistoryFilters: HistoryFilters = {
  wallet: "", tx: "", from: "", to: "", side: "", status: "", dex: "", path: "",
  mode: "all", minUsd: "", maxUsd: "", order: "desc",
};

/** Parse explicit local fields without browser-locale parsing or date rollover. */
export function parseHistoryDateTime(value: string): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (!match) throw new Error("Use date YYYY-MM-DD and 24-hour time HH:mm or HH:mm:ss");
  const [year, month, day, hour, minute, second] = match.slice(1).map(part => Number(part ?? 0));
  if (year! < 1970 || month! < 1 || month! > 12 || day! < 1 || day! > 31 || hour! > 23 || minute! > 59 || second! > 59) {
    throw new Error("Enter a valid date and time");
  }
  const date = new Date(year!, month! - 1, day!, hour!, minute!, second!);
  if (date.getFullYear() !== year || date.getMonth() !== month! - 1 || date.getDate() !== day ||
      date.getHours() !== hour || date.getMinutes() !== minute || date.getSeconds() !== second) {
    throw new Error("Enter a valid date and time in your time zone");
  }
  return date.getTime();
}

/** Monday-first calendar cells; date labels and parsing never depend on locale. */
export function historyCalendarDays(year: number, month: number): Array<{ date: string; day: number } | null> {
  const offset = (new Date(year, month, 1).getDay() + 6) % 7;
  const count = new Date(year, month + 1, 0).getDate();
  return Array.from({ length: Math.ceil((offset + count) / 7) * 7 }, (_, index) => {
    const day = index - offset + 1;
    return day < 1 || day > count ? null : { day, date: `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}` };
  });
}

/** Local times are converted to UTC milliseconds for the API. */
export function historyFilterQuery(filters: HistoryFilters): string {
  const query = new URLSearchParams();
  for (const name of ["wallet", "tx", "side", "status", "dex", "path", "mode", "order"] as const) {
    const value = filters[name].trim();
    if (value) query.set(name, value);
  }
  const times: Partial<Record<"from" | "to", number>> = {};
  for (const name of ["from", "to"] as const) {
    if (!filters[name]) continue;
    const value = parseHistoryDateTime(filters[name]);
    // Include the whole selected end second (or minute when seconds omitted).
    times[name] = value + (name === "to" ? (filters[name].length === 16 ? 59_999 : 999) : 0);
    query.set(name, String(times[name]));
  }
  if (times.from !== undefined && times.to !== undefined && times.from > times.to) {
    throw new Error("Start time must be before end time");
  }
  for (const name of ["minUsd", "maxUsd"] as const) {
    if (!filters[name].trim()) continue;
    const value = Number(filters[name]);
    if (!Number.isFinite(value) || value < 0 || value > 1e18) throw new Error("Enter a valid USD range");
    query.set(name, String(value));
  }
  if (query.has("minUsd") && query.has("maxUsd") && Number(query.get("minUsd")) > Number(query.get("maxUsd"))) {
    throw new Error("Minimum USD must not exceed maximum USD");
  }
  return query.toString();
}

export function localHistoryTime(time: number): string {
  const date = new Date(time);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export function historyTxUrl(chain: string, signature: string): string | null {
  const explorers: Record<string, string> = {
    base: "https://basescan.org", ethereum: "https://etherscan.io",
    robinhood: "https://robinhoodchain.blockscout.com", bnb: "https://bscscan.com",
    ink: "https://explorer.inkonchain.com", arc: "https://arc-scan.org",
  };
  if (chain === "solana") return /^[1-9A-HJ-NP-Za-km-z]{64,90}$/.test(signature) ? `https://solscan.io/tx/${signature}` : null;
  return /^0x[0-9a-fA-F]{64}$/.test(signature) && explorers[chain] ? `${explorers[chain]}/tx/${signature}` : null;
}
